import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import './theme.css'
import App from './App.tsx'
import { createClient, Provider, cacheExchange, fetchExchange } from 'urql'
import { AuthProvider } from './context/auth-provider.tsx'
import * as Sentry from '@sentry/react'
import posthog from 'posthog-js'
import { GoogleOAuthProvider } from '@react-oauth/google'
import { getInMemoryToken, silentRefresh } from './lib/auth'

if (import.meta.env.VITE_POSTHOG_KEY) {
  posthog.init(import.meta.env.VITE_POSTHOG_KEY, {
    api_host: import.meta.env.VITE_POSTHOG_HOST,
    person_profiles: 'identified_only',
  })
}

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.1,
  })
}

const apiBase = import.meta.env.VITE_API_URL || 'http://localhost:8080'

const client = createClient({
  url: `${apiBase}/query`,
  // 1. Inject access token from memory (never localStorage)
  fetchOptions: () => {
    const token = getInMemoryToken()
    return {
      headers: { Authorization: token ? `Bearer ${token}` : '' },
      credentials: 'include', // needed for browser to send httpOnly refresh cookie
    }
  },
  // 2. On 401/403 — try silent refresh via httpOnly cookie before logging out.
  // When refresh fails we dispatch auth:logout (which redirects to /login) and
  // return an empty 200 so urql never surfaces a raw "Invalid token" network error.
  fetch: async (input: RequestInfo | URL, init?: RequestInit) => {
    const response = await fetch(input, { ...init, credentials: 'include' })
    if (response.status === 401 || response.status === 403) {
      const newToken = await silentRefresh()
      if (newToken) {
        return fetch(input, {
          ...init,
          credentials: 'include',
          headers: {
            ...(init?.headers ?? {}),
            Authorization: `Bearer ${newToken}`,
          },
        })
      }
      // Refresh failed — session is truly expired. Redirect to login.
      // Return a silent empty response so urql doesn't flash a network error
      // while the redirect is in flight.
      window.dispatchEvent(new Event('auth:logout'))
      return new Response(JSON.stringify({ data: null, errors: [] }), {
        status: 200,
        headers: { 'Content-Type': 'application/json' },
      })
    }
    return response
  },
  exchanges: [cacheExchange, fetchExchange],
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID || ''}>
      <Provider value={client}>
        <AuthProvider>
          <App />
        </AuthProvider>
      </Provider>
    </GoogleOAuthProvider>
  </StrictMode>,
)
